import React, {
    useEffect,
    useRef
} from "react";

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";

export default function OrganisationExtremeChart({
    chartId,
    title,
    color,
    data,
    zoomStart,
    zoomEnd,
    cursorDate,
    onCursorChange,
    onZoomChange,
    isMaster,
    currentFrame,
    onFrameSelect
}) {

    const chartRef =
        useRef(null);

    /*
     * MAIN CHART
     */
    useEffect(function () {

        if (
            !data
            || data.length === 0
        ) {
            return;
        }

        /*
         * CLEANUP
         */
        if (chartRef.current) {

            try {
                chartRef.current.dispose();
            }
            catch (e) {}

        }

        /*
         * CHART
         */
        const chart =
            am4core.create(
                chartId,
                am4charts.XYChart
            );

        chartRef.current =
            chart;

        chart.paddingTop = 10;
        chart.paddingRight = 12;
        chart.paddingBottom = 0;
        chart.maskBullets = false;

        /*
         * SORT DATA
         */
        chart.data =
            data.slice().sort(
                function (a, b) {

                    return (
                        new Date(a.date)
                        - new Date(b.date)
                    );

                }
            );

        /*
         * X AXIS
         */
        const dateAxis =
            chart.xAxes.push(
                new am4charts.DateAxis()
            );

        dateAxis.baseInterval = {
            timeUnit: "hour",
            count: 1
        };

        dateAxis.skipEmptyPeriods =
            true;

        dateAxis.renderer.grid.template.strokeOpacity =
            0.03;

        dateAxis.renderer.labels.template.disabled =
            true;

        dateAxis.tooltip.disabled =
            true;

        /*
         * Y AXIS
         */
        const valueAxis =
            chart.yAxes.push(
                new am4charts.ValueAxis()
            );

        valueAxis.min = 0;
        valueAxis.max = 100;
        valueAxis.strictMinMax =
            true;

        valueAxis.renderer.grid.template.strokeOpacity =
            0.03;

        valueAxis.renderer.labels.template.disabled =
            true;

        valueAxis.tooltip.disabled =
            true;

        /*
         * SERIES
         */
        const series =
            chart.series.push(
                new am4charts.ColumnSeries()
            );

        series.dataFields.dateX =
            "date";

        series.dataFields.valueY =
            "utilisation";

        series.columns.template.width =
            am4core.percent(55);

        series.strokeOpacity =
            0;

        /*
         * COLOURS
         */
        series.columns.template.propertyFields.fill =
            "columnColor";

        series.columns.template.propertyFields.stroke =
            "columnColor";

        /*
         * STYLE
         */
        series.columns.template.fillOpacity =
            0.92;

        series.columns.template.strokeOpacity =
            1;

        series.columns.template.strokeWidth =
            1;

        series.columns.template.cornerRadiusTopLeft =
            2;

        series.columns.template.cornerRadiusTopRight =
            2;

        /*
         * TOOLTIP
         */
        series.tooltip.getFillFromObject =
            false;

        series.tooltip.background.fill =
            am4core.color("#111");

        series.tooltip.background.stroke =
            am4core.color(color);

        series.tooltip.background.strokeWidth =
            1;

        series.tooltip.background.cornerRadius =
            8;

        series.tooltip.label.fontSize =
            11;

        series.tooltip.label.fill =
            am4core.color("#ffffff");

        series.columns.template.tooltipText =
            "[bold]{date.formatDate('dd MMM HH:mm')}[/]\n"
            + "{valueY.formatNumber('#.0')}%";

        /*
         * TOOLTIP COLOUR OVERRIDE
         */
        series.columns.template.events.on(
            "over",
            function (ev) {

                const dataItem =
                    ev.target.dataItem;

                if (
                    !dataItem
                    || !dataItem.dataContext
                ) {
                    return;
                }

                const tooltipColor =
                    dataItem.dataContext.columnColor
                    || "#111";

                series.tooltip.background.fill =
                    am4core.color(
                        tooltipColor
                    );

                series.tooltip.background.stroke =
                    am4core.color(
                        tooltipColor
                    );

            }
        );

        /*
         * CLICK
         */
        series.columns.template.events.on(
            "hit",
            function (ev) {

                if (!onFrameSelect) {
                    return;
                }

                onFrameSelect(
                    ev.target.dataItem.dataContext
                );

            }
        );

        /*
         * CURSOR
         */
        chart.cursor =
            new am4charts.XYCursor();

        chart.cursor.snapToSeries =
            series;

        chart.cursor.lineY.disabled =
            true;

        chart.cursor.lineX.stroke =
            am4core.color("#ffffff");

        chart.cursor.lineX.strokeOpacity =
            0.18;

        chart.cursor.lineX.strokeWidth =
            1;

        chart.cursor.lineX.strokeDasharray =
            "3,3";

        /*
         * MASTER = ZOOM
         * CHILD = NONE
         */
        if (isMaster) {

            chart.cursor.behavior =
                "zoomX";

        }
        else {

            chart.cursor.behavior =
                "none";

        }

        /*
         * SHARED CURSOR DATE
         *
         * IMPORTANT:
         * ANY chart can publish cursor date
         */
        chart.cursor.events.on(
            "cursorpositionchanged",
            function () {

                if (!onCursorChange) {
                    return;
                }

                const date =
                    dateAxis.positionToDate(
                        chart.cursor.xPosition
                    );

                onCursorChange(date);

            }
        );

		/*
		 * MASTER SCROLLBAR
		 */
		if (isMaster) {

		    chart.scrollbarX =
		        new am4core.Scrollbar();

		    /*
		     * PREMIUM DARK STYLE
		     */
		    chart.scrollbarX.background.fill =
		        am4core.color("#111");

		    chart.scrollbarX.background.fillOpacity =
		        0.55;

		    chart.scrollbarX.thumb.background.fill =
		        am4core.color("#2b2b2b");

		    chart.scrollbarX.thumb.background.fillOpacity =
		        0.95;

		    chart.scrollbarX.thumb.background.stroke =
		        am4core.color("#555");

		    chart.scrollbarX.thumb.background.strokeWidth =
		        1;

		    /*
		     * GRIPS
		     */
		    chart.scrollbarX.startGrip.background.fill =
		        am4core.color("#1a1a1a");

		    chart.scrollbarX.endGrip.background.fill =
		        am4core.color("#1a1a1a");

		    chart.scrollbarX.startGrip.background.stroke =
		        am4core.color("#666");

		    chart.scrollbarX.endGrip.background.stroke =
		        am4core.color("#666");

		    chart.scrollbarX.startGrip.icon.stroke =
		        am4core.color("#aaa");

		    chart.scrollbarX.endGrip.icon.stroke =
		        am4core.color("#aaa");

		    chart.scrollbarX.startGrip.hoverable =
		        false;

		    chart.scrollbarX.endGrip.hoverable =
		        false;

		    /*
		     * ZOOM SYNC
		     */
		    dateAxis.events.on(
		        "selectionextremeschanged",
		        function (ev) {

		            if (!onZoomChange) {
		                return;
		            }

		            console.log(
		                "MASTER ZOOM",
		                ev.target.minZoomed,
		                ev.target.maxZoomed
		            );

		            onZoomChange({
		                start:
		                    new Date(ev.target.minZoomed),
		                end:
		                    new Date(ev.target.maxZoomed)
		            });

		        }
		    );

		}

        /*
         * REMOVE PADDING
         */
        chart.leftAxesContainer.layout =
            "absolute";

        /*
         * CLEANUP
         */
        return function () {

            try {
                chart.dispose();
            }
            catch (e) {}

        };

    }, [
        data,
        color,
        chartId,
        isMaster
    ]);

    /*
     * SHARED TOOLTIP SYNC
     */
    useEffect(function () {

        if (
            !cursorDate
            || !chartRef.current
        ) {
            return;
        }

        const chart =
            chartRef.current;

        const series =
            chart.series.getIndex(0);

        if (!series) {
            return;
        }

        let closest =
            null;

        let closestDiff =
            999999999;

        series.dataItems.each(function (di) {

            if (!di.dateX) {
                return;
            }

            const diff =
                Math.abs(
                    di.dateX.getTime()
                    - cursorDate.getTime()
                );

            if (diff < closestDiff) {

                closest =
                    di;

                closestDiff =
                    diff;

            }

        });

        if (closest) {

            try {

                series.showTooltipAtDataItem(
                    closest
                );

            }
            catch (e) {}

        }

    }, [
        cursorDate
    ]);

    /*
     * CHILD ZOOM SYNC
     */
    useEffect(function () {

        if (
            !chartRef.current
            || isMaster
            || !zoomStart
            || !zoomEnd
        ) {
            return;
        }

        const chart =
            chartRef.current;

        const dateAxis =
            chart.xAxes.getIndex(0);

        if (!dateAxis) {
            return;
        }

        dateAxis.zoomToDates(
            new Date(zoomStart),
            new Date(zoomEnd),
            false,
            true
        );

    }, [
        zoomStart,
        zoomEnd,
        isMaster
    ]);

    /*
     * CURRENT FRAME MARKER
     */
    useEffect(function () {

        if (
            !chartRef.current
            || !currentFrame
        ) {
            return;
        }

        const chart =
            chartRef.current;

        const dateAxis =
            chart.xAxes.getIndex(0);

        if (!dateAxis) {
            return;
        }

        while (
            dateAxis.axisRanges.length > 0
        ) {

            try {

                dateAxis.axisRanges
                    .removeIndex(0)
                    .dispose();

            }
            catch (e) {
                break;
            }

        }

        const markerDate =
            new Date(
                currentFrame.date
                + "T"
                + currentFrame.startTime
                + ":00"
            );

        const range =
            dateAxis.axisRanges.create();

        range.date =
            markerDate;

        range.grid.stroke =
            am4core.color("#ffffff");

        range.grid.strokeOpacity =
            0.28;

        range.grid.strokeWidth =
            2;

    }, [
        currentFrame
    ]);

    return (
        <div
            style={{
                marginBottom: "18px"
            }}
        >

            {title && (
                <div
                    style={{
                        color: "white",
                        fontSize: "13px",
                        fontWeight: 600,
                        marginBottom: "6px"
                    }}
                >
                    {title}
                </div>
            )}

            <div
                id={chartId}
                style={{
                    width: "100%",
                    height:
                        isMaster
                        ? "150px"
                        : "120px",
                    borderRadius: "12px",
                    overflow: "hidden",
                    background:
                        "rgba(255,255,255,0.03)",
                    border:
                        "1px solid rgba(255,255,255,0.05)"
                }}
            />

        </div>
    );

}